import React, { createContext, useContext, useRef, useState } from "react";
import { AsynchronousNotificationList } from "./components/core/AsynchronousNotificationList/AsynchronousNotificationList";
import { AsynchronousNotification } from "./models/AsynchronousNotification";

export type NotificationSelector = {
  id?: string;
  /**
   * Selects a notification containing, but not limited to, a set of tags.
   */
  tags?: string[];
}

export type AsynchronousNotificationContextType = {
  notifications: AsynchronousNotification[];
  delete: (selector: NotificationSelector) => void;
  notify: (notification: AsynchronousNotification) => void;
};

/**
 * Stores and provides asynchronous notifications.
 */
export const AsynchronousNotificationContext = createContext<AsynchronousNotificationContextType>(null);

export const AsynchronousNotificationContextProvider: React.FC = ({ children }) => {
  const [notifications, setNotifications] = useState<AsynchronousNotification[]>([]);

  //  List of setTimeout results, where each setTimeout is called when notification is created.
  //  Some hooks might delete a notification, then create an identical, then delete it again, all
  //  in 3 render cycles, due to state changes. This debounce system prevents the UI from being affected.
  const debounceTimeoutIds = useRef<{ [notificationId: string]: any }>({});

  function deleteNotification(selector: NotificationSelector) {
    setNotifications(notifications => notifications.map(notification => {
      if (
        (selector.id && notification.id === selector.id) ||
        (selector.tags?.length && selector.tags.every(tag => notification.tags.includes(tag)))
      ) {
        clearTimeout(debounceTimeoutIds.current[notification.id]);
        debounceTimeoutIds.current[notification.id] = null; //  Prevent the state update which adds the notification.
        return notification.read();
      }

      return notification
    }));
  }

  return (
    <AsynchronousNotificationContext.Provider value={{
      notify: notification => {
        debounceTimeoutIds.current[notification.id] = setTimeout(() => {
          setNotifications(notifications => {
            if (notifications.find(previousNotification =>
              !previousNotification.isRead && previousNotification.id === notification.id)) {
              //  Do not display duplicate notifications.
              return notifications;
            }

            if (debounceTimeoutIds.current[notification.id] === null) {
              //  Canceled.
              return notifications;
            }

            return [notification, ...notifications];
          });
        }, 50);
      },

      delete: deleteNotification,
      notifications
    }}>
      {children}
    </AsynchronousNotificationContext.Provider>
  )
}

export const ManagedAsynchronousNotificationList: React.FC = () => {
  const { delete: deleteNotification, notifications } = useContext(AsynchronousNotificationContext);

  return (
    <AsynchronousNotificationList
      notifications={notifications}
      onClose={notification => deleteNotification({ id: notification.id })} />
  )
}